import { AfterContentChecked, AfterViewInit, Component, ErrorHandler, HostListener, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'
import { FieldErrors, FormValidationService } from '../_jaettu-angular/service/form-validation.service'

import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { PalautaSalasanaPyynto, PalautaSalasanaVastaus } from '@lemon/core-firebase/model/common'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

interface SalasananPalautusLomake {
  password: string
}

@Component({
  templateUrl: './reset.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ResetComponent implements OnInit, AfterViewInit, AfterContentChecked {
  @ViewChild(MatInput) passwordInput: MatInput

  lomake = form(signal<SalasananPalautusLomake>({ password: '' }), polku => {
    required(polku.password)
    validoiReaktiivisesti(polku.password, LemonaidValidators.passwordComplexityValidator)
  })
  fieldErrors: FieldErrors = {}
  readonly commonError = signal<string | null>(null)
  readonly success = signal(false)
  online = true
  year = new Date().getFullYear()

  // private mode: 'resetPassword' | 'recoverEmail' | 'verifyEmail' = null
  private oobCode: string = null

  constructor(
    private _dialog: MatDialog,
    private _router: Router,
    private _route: ActivatedRoute,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    // https://lemonaid-9ef4a.firebaseapp.com/__/auth/action?mode=resetPassword&oobCode=dho90ylQKXxQuO8GEoikZeoxYL5h3Aq7fW_AirIjHOgAAAFiTlaaXQ&apiKey=AIzaSyCf2xZVo-4u5HeQmDp5lSM7asfPpJAEW9g
    this._route.queryParams.subscribe(params => {
      // this.mode = params['mode']
      this.oobCode = params['oobCode']
    })
  }

  ngAfterContentChecked() {
    this.fieldErrors = this._validationService.updateValidationStatus('login', this.lomake)
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.passwordInput.focus()
    }, 0)
  }

  virheet(): { [laji: string]: boolean } {
    if (this.lomake) {
      const pwdField = this.lomake.password
      if (pwdField) {
        return Object.fromEntries(
          pwdField()
            .errors()
            .map(virhe => [virhe.kind, true]),
        )
      }
    }
    return {}
  }

  setLoginProcessStarted() {
    this._ladataanService.aloitaLataaminen()
  }

  setLoginProcessEnded() {
    this._ladataanService.lopetaLataaminen()
  }

  @HostListener('document:keydown.esc')
  back() {
    this._router.navigate(['/kirjaudu'])
  }

  @HostListener('document:keydown.enter')
  defaultAction() {
    if (this.success()) {
      this.login()
    } else {
      this.changePassword()
    }
  }

  changePassword() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.setLoginProcessStarted()
    const theCode = this.oobCode || 'no'

    this.commonError.set(null)
    this.fieldErrors = {}

    const req: PalautaSalasanaPyynto = {
      newPassword: this.lomake.password().value(),
      oobCode: theCode,
    }
    this._firebaseLemonaid
      .functionsCall<PalautaSalasanaPyynto, PalautaSalasanaVastaus>('passwordResetChangePassword', req)
      .then(result => {
        const code = result.e
        if (code) {
          switch (code) {
            case 'auth/expired-action-code':
              this.commonError.set(this._translationService.lokalisoi('reset.virheet.koodi-vanhentunut'))
              break
            case 'auth/invalid-action-code':
              this.commonError.set(this._translationService.lokalisoi('reset.virheet.koodi-ei-oikea'))
              break
            case 'auth/user-disabled':
              this.commonError.set(this._translationService.lokalisoi('reset.virheet.kayttaja-inaktiivinen'))
              break
            case 'auth/user-not-found':
              this.commonError.set(this._translationService.lokalisoi('reset.virheet.kayttaja-ei-loydy'))
              break
            case 'auth/weak-password':
              this.commonError.set(this._translationService.lokalisoi('reset.virheet.salasana-heikko'))
              break
            default:
              this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          }
          this.ngAfterContentChecked()
          this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
          this.setLoginProcessEnded()
        } else {
          this.success.set(true)
          this.setLoginProcessEnded()
        }
      })
      .catch(err => {
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this._errorHandler.handleError(err)
        this.setLoginProcessEnded()
      })
  }

  login() {
    this._kayttajaService.logout().then(() => {
      this._router.navigate(['/kirjaudu'])
    })
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }
}
